<template>
  <div class="form-fields">
    <template v-for="field in fields">
      <div
        :class="[
          'form-fields__item',
          `form-fields__item--span-${field.colSpan}`,
          { 'form-fields__item--hidden': field.isHidden },
          { 'form-fields__item--error': field.error },
        ]"
        :key="field.id"
      >
        <slot
          :name="field.name"
          :field="field"
          :form-context="formContext"
          :value="field.value"
          :error="field.error"
          :disabled="field.disabled"
          :error-text="field.errorText"
          :description="field.description"
          :input="value => $emit('input', field, value)"
          :blur="() => $emit('blur', field)"
          :focus="() => $emit('focus', field)"
        >
          <TaskField
            v-if="field.type === FORM_FIELD_VIEW.STUB_TEXT && field.addParams[FORM_ADD_PARAMS.maximum]"
            class="form-fields__stub-text"
            :fields="fields"
            :add-params="field.addParams"
            :form-context="formContext.maximum"
          ></TaskField>

          <FormField v-else class="form-fields__field" :field="field">
            <template v-if="field.isHidden" #hidden-field>
              <input type="hidden" :value="field.value" />
            </template>
            <template v-else>
              <UiInput
                v-if="field.type === FORM_FIELD_VIEW.TEXT || field.type === FORM_FIELD_VIEW.NUMBER"
                v-bind="field.addParams[FORM_ADD_PARAMS.fieldProps]"
                class="form-fields__input-field"
                :id="field.labelForId"
                :value="field.value"
                :placeholder="field.hintField"
                :disabled="field.disabled"
                :error="field.error"
                :valid="field.valid"
                @input="$emit('input', field, $event)"
                @blur="$emit('blur', field)"
                @focus="$emit('focus', field)"
              />

              <UiMaskedInput
                v-if="field.type === FORM_FIELD_VIEW.PHONE"
                v-bind="field.addParams[FORM_ADD_PARAMS.fieldProps]"
                class="form-fields__masked-input-field"
                :id="field.labelForId"
                :value="field.value"
                :mask="field.mask"
                :placeholder="field.hintField"
                :disabled="field.disabled"
                :error="field.error"
                @input="$emit('input', field, $event)"
                @blur="$emit('blur', field)"
                @focus="$emit('focus', field)"
              />

              <Textarea
                v-else-if="field.type === FORM_FIELD_VIEW.TEXTAREA"
                v-bind="field.addParams[FORM_ADD_PARAMS.fieldProps]"
                class="form-fields__textarea-field"
                :id="field.labelForId"
                :value="field.value"
                :placeholder="field.hintField"
                :disabled="field.disabled"
                :error="field.error"
                :valid="field.valid"
                @input="$emit('input', field, $event)"
                @blur="$emit('blur', field)"
                @focus="$emit('focus', field)"
              />

              <UiSelect
                v-else-if="field.typeField === FIELD_TYPES.SELECT_ONE"
                v-bind="field.addParams[FORM_ADD_PARAMS.fieldProps]"
                class="form-fields__select-field"
                :value="field.value"
                :options="field.list"
                :disabled="field.disabled"
                :error="field.error"
                close-on-select
                clearable
                @input="$emit('input', field, $event)"
                @blur="$emit('blur', field)"
                @open="$emit('focus', field)"
              />

              <UiSelect
                v-else-if="field.typeField === FIELD_TYPES.SELECT_MULTI"
                v-bind="field.addParams[FORM_ADD_PARAMS.fieldProps]"
                class="form-fields__multi-select-field"
                :value="field.value"
                :options="field.list"
                :disabled="field.disabled"
                :error="field.error"
                multi
                @input="$emit('input', field, $event)"
                @blur="$emit('blur', field)"
                @open="$emit('focus', field)"
              />

              <UiAsyncSelect
                v-else-if="field.typeField === FIELD_TYPES.SELECT_SEARCHABLE"
                v-bind="field.addParams[FORM_ADD_PARAMS.fieldProps]"
                class="form-fields__async-select-field"
                :value="field.value"
                :field-id="field.id"
                :entity-id="formContext.entityId"
                :search-url="field.searchUrl"
                :disabled="field.disabled"
                :error="field.error"
                searchable
                close-on-select
                @input="$emit('input', field, $event)"
                @blur="$emit('blur', field)"
                @open="$emit('focus', field)"
              />

              <CheckboxGroup
                v-else-if="field.type === FORM_FIELD_VIEW.CHECKBOX"
                v-bind="field.addParams[FORM_ADD_PARAMS.fieldProps]"
                class="form-fields__checkbox-group-field"
                :value="field.value"
                :list="field.list"
                :disabled="field.disabled"
                :error="field.error"
                @input="$emit('input', field, $event)"
                @change="$emit('blur', field)"
              />

              <CheckboxGroup
                v-else-if="field.type === FORM_FIELD_VIEW.CHECKBOX_IMAGES"
                v-bind="field.addParams[FORM_ADD_PARAMS.fieldProps]"
                class="form-fields__image-group-field"
                view="card"
                :value="field.value"
                :list="field.list"
                :disabled="field.disabled"
                :error="field.error"
                @input="$emit('input', field, $event)"
                @change="$emit('blur', field)"
              />

              <RadioGroup
                v-else-if="field.type === FORM_FIELD_VIEW.RADIO"
                v-bind="field.addParams[FORM_ADD_PARAMS.fieldProps]"
                class="form-fields__radio-group-field"
                :value="field.value"
                :list="field.list"
                :name="field.id"
                :disabled="field.disabled"
                :error="field.error"
                @input="$emit('input', field, $event)"
                @change="$emit('blur', field)"
              />

              <DatePicker
                v-else-if="field.type === FORM_FIELD_VIEW.DATEPICKER"
                v-bind="field.addParams[FORM_ADD_PARAMS.fieldProps]"
                class="form-fields__date-picker-field"
                :value="field.value"
                :placeholder="field.hintField"
                :disabled="field.disabled"
                :error="field.error"
                :locale="formContext.locale"
                @input="$emit('input', field, $event)"
                @blur="$emit('blur', field)"
                @focus="$emit('focus', field)"
              />

              <Dropzone
                v-else-if="field.type === FORM_FIELD_VIEW.DROPZONE"
                v-bind="field.addParams[FORM_ADD_PARAMS.fieldProps]"
                class="form-fields__dropzone-field"
                :value="field.value"
                :options="field.options"
                :disabled="field.disabled"
                :error="field.error"
                @input="$emit('input', field, $event)"
                @change="$emit('blur', field)"
              >
                <p>
                  {{ field.hintField }}
                </p>
              </Dropzone>

              <CityInput
                v-else-if="field.type === FORM_FIELD_VIEW.DADATA_CITY"
                v-bind="field.addParams[FORM_ADD_PARAMS.fieldProps]"
                class="form-fields__city-input"
                :id="field.labelForId"
                :token="$root.siteDadataKey"
                :value="field.value"
                :placeholder="field.hintField"
                :error="field.error"
                :valid="field.valid"
                :disabled="field.disabled"
                @update:value="value => $emit('input', field, value)"
                @focus="$emit('focus', field)"
              />

              <OrganizationInput
                v-else-if="field.type === FORM_FIELD_VIEW.DADATA_ORG"
                v-bind="field.addParams[FORM_ADD_PARAMS.fieldProps]"
                class="form-fields__organization-input"
                :id="field.labelForId"
                :token="$root.siteDadataKey"
                :value="field.value"
                :placeholder="field.hintField"
                :error="field.error"
                :valid="field.valid"
                :disabled="field.disabled || (!formContext.isAuth && !formContext.location)"
                :location="formContext.location"
                @update:value="value => $emit('input', field, value)"
                @focus="$emit('focus', field)"
              />

              <PersonalDataAgreement
                v-else-if="field.type === FORM_FIELD_VIEW.PERSONAL_DATA_AGREEMENT"
                v-bind="field.addParams[FORM_ADD_PARAMS.fieldProps]"
                class="form-fields__agreement-field"
                :value="field.value"
                :error="field.error"
                @input="$emit('input', field, $event)"
              ></PersonalDataAgreement>
            </template>
          </FormField>
        </slot>
      </div>
    </template>
  </div>
</template>

<script>
import { FORM_ADD_PARAMS, FORM_FIELD_TYPE, FORM_FIELD_VIEW } from '@shared/const/form/form.field'
import FormField from '@shared/ui/public/forms/components/FormField.vue'
import UiInput from '@shared/ui/public/forms/fields/UiInput.vue'
import UiMaskedInput from '@shared/ui/public/forms/fields/UiMaskedInput.vue'
import Textarea from '@shared/ui/public/forms/fields/Textarea.vue'
import Checkbox from '@shared/ui/public/forms/fields/Checkbox.vue'
import CheckboxGroup from '@shared/ui/public/forms/fields/CheckboxGroup.vue'
import CheckboxImage from '@shared/ui/public/forms/fields/CheckboxImage.vue'
import RadioGroup from '@shared/ui/public/forms/fields/RadioGroup.vue'
import UiSelect from '@shared/ui/public/forms/fields/UiSelect.vue'
import UiAsyncSelect from '@shared/ui/public/forms/fields/UiAsyncSelect.vue'
import DatePicker from '@shared/ui/public/forms/fields/DatePicker.vue'
import Dropzone from '@shared/ui/public/forms/fields/Dropzone.vue'
import PersonalDataAgreement from '@shared/ui/public/forms/fields/PersonalDataAgreement.vue'
import CityInput from '@shared/ui/public/forms/fields/CityInput.vue'
import TaskField from '@shared/ui/public/forms/fields/TaskField.vue'
import OrganizationInput from '@shared/ui/public/forms/fields/OrganizationInput.vue'

export default {
  name: 'FormFields',
  emits: ['input', 'blur', 'focus'],
  components: {
    FormField,
    UiInput,
    UiMaskedInput,
    Textarea,
    Checkbox,
    CheckboxGroup,
    CheckboxImage,
    RadioGroup,
    UiSelect,
    UiAsyncSelect,
    DatePicker,
    Dropzone,
    PersonalDataAgreement,
    CityInput,
    TaskField,
    OrganizationInput,
  },
  props: {
    fields: {
      type: Array,
      default: () => [],
    },
    formContext: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    FORM_ADD_PARAMS() {
      return FORM_ADD_PARAMS
    },
    FORM_FIELD_VIEW() {
      return FORM_FIELD_VIEW
    },
    FIELD_TYPES() {
      return FORM_FIELD_TYPE
    },
  },
}
</script>

<style lang="sass">
@import "~@src/legacy/sass/params"

.form-fields
  display: grid
  grid-template-columns: repeat(12, 1fr)
  row-gap: 10px
  column-gap: 24px

  @media (max-width: $mobile-size)
    grid-template-columns: 1fr

  &__item
    grid-column: span 12

    &--span-10
      grid-column: span 10

    &--span-8
      grid-column: span 6

    &--span-6
      grid-column: span 6

    &--span-4
      grid-column: span 4

    &--span-2
      grid-column: span 2

    &--hidden
      display: none

    @media (max-width: $mobile-size)
      grid-column: span 1

  &__label
    display: inline-block
    margin-bottom: 8px
    color: var(--field-text-color, $black)
    font-size: 13px
    line-height: 1.3

  &__checkbox-field
    width: fit-content

  &__required
    margin-left: 4px
    color: var(--field-asterisk-color, #EF7171)

  &__error
    margin-top: 6px
    font-size: 12px
    line-height: normal
    color: var(--field-error-text-color, #EF7171)
</style>



// WEBPACK FOOTER //
// src/modules/shared/ui/public/forms/components/FormFields.vue